import { ApiError, getAccessToken } from './api';

/**
 * Скачивание вложения. Картинка лежит по открытому адресу, и её достаточно
 * отдать браузеру ссылкой. Документ сервер отдаёт только участнику заказа —
 * а обычная ссылка заголовок `Authorization` не несёт и упирается в 401,
 * поэтому за ним идём запросом и подставляем файл уже из памяти.
 */
export async function downloadAttachment(url: string, fileName: string): Promise<void> {
  if (!url.startsWith('/v1/documents/')) {
    saveAs(url, fileName);
    return;
  }

  const token = getAccessToken();
  const response = await fetch(url, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new ApiError(
      response.status,
      response.status === 404 ? 'DOCUMENT_NOT_FOUND' : 'DOWNLOAD_FAILED',
      response.status === 404 ? 'Файл не найден' : 'Не удалось скачать файл',
    );
  }

  const objectUrl = URL.createObjectURL(await response.blob());
  try {
    saveAs(objectUrl, fileName);
  } finally {
    // Отзываем не сразу: Safari успевает начать скачивание только после клика.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }
}

function saveAs(url: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  link.click();
}

/**
 * Выгрузка CSV. Токен уходит заголовком, а не параметром адреса: ссылка с
 * ключом оседает в истории, логах и referrer. Готовый файл отдаётся браузеру
 * через `data:`-URL — во встроенном браузере Telegram `blob:` не скачивается.
 */
export async function downloadCsv(path: string, fallbackName: string): Promise<void> {
  const token = getAccessToken();
  const response = await fetch(path, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new ApiError(response.status, 'EXPORT_FAILED', 'Не удалось собрать выгрузку');
  }

  const text = await response.text();
  const name = fileNameFrom(response.headers.get('content-disposition')) ?? fallbackName;

  saveAs(`data:text/csv;charset=utf-8,${encodeURIComponent(text)}`, name);
}

/** Имя файла собирает сервер, поэтому берём его из заголовка ответа. */
function fileNameFrom(disposition: string | null): string | null {
  const match = disposition?.match(/filename="([^"]+)"/);

  return match?.[1] ?? null;
}
